import { format } from 'date-fns';

import StatusBadge from '@/components/status-badge';
import { Badge } from '@/components/ui/badge';
import { GanttFeature } from '@/features/end-of-lease/gantt';

import { ProjectTask, ProjectTaskParty, ProjectTaskPriority } from './types';

const partyColors: Record<
    ProjectTaskParty,
    { bg: string; border: string; text: string }
> = {
    [ProjectTaskParty.Lessor]: {
        bg: 'bg-blue-600',
        border: 'border-blue-800',
        text: 'text-blue-100',
    },
    [ProjectTaskParty.CAMO]: {
        bg: 'bg-emerald-600',
        border: 'border-emerald-800',
        text: 'text-emerald-100',
    },
    [ProjectTaskParty.MRO]: {
        bg: 'bg-amber-600',
        border: 'border-amber-800',
        text: 'text-amber-100',
    },
};

interface Props {
    feature: GanttFeature;
    tasks: ProjectTask[];
    party: ProjectTaskParty;
    onTaskClick: (task: ProjectTask) => void;
}

export function TimelineCard({ feature, tasks, party, onTaskClick }: Props) {
    const getTask = (featureId: string): ProjectTask | undefined => {
        return tasks.find((a) => a.id.toString() === featureId);
    };
    const task = getTask(feature.id);
    const colors = partyColors[party];

    return (
        <div
            className={`group flex flex-col justify-center w-full h-full px-2 ${colors.bg} ${colors.border} border rounded-md overflow-hidden`}
            onClick={() => task && onTaskClick(task)}
            data-testid="TimelineCard"
        >
            <div className="flex items-center gap-1 min-w-0">
                <div className="text-xs font-semibold text-white truncate flex-1 min-w-0">
                    {feature.name}
                </div>
                <div className="hidden [@media(min-width:80px)]:block">
                    <StatusBadge
                        status={
                            feature.status.id as
                                | 'completed'
                                | 'in-progress'
                                | 'on-hold'
                                | 'not-started'
                        }
                        size="sm"
                        showIcon={false}
                    />
                </div>
            </div>
            {task && (
                <div className="hidden [@media(min-width:120px)]:block space-y-0.5 mt-0.5">
                    <div
                        className={`flex items-center justify-between text-[8px] ${colors.text} gap-1`}
                    >
                        <span className="truncate flex-1 min-w-0">
                            {format(new Date(task.startDate), 'MMM d')} -{' '}
                            {format(new Date(task.endDate), 'MMM d')}
                        </span>
                        <Badge
                            variant={
                                task.priority === ProjectTaskPriority.High
                                    ? 'destructive'
                                    : task.priority ===
                                        ProjectTaskPriority.Medium
                                      ? 'warning'
                                      : 'default'
                            }
                            className="text-[8px] h-3 px-1 mt-1 whitespace-nowrap"
                        >
                            {task.priority.toUpperCase()}
                        </Badge>
                    </div>
                </div>
            )}
        </div>
    );
}
